import { lerp } from '@softgames/math-utils';
import { type Container } from 'pixi.js';

interface RowsConfig {
  containerWidth: number;
  containerHeight: number;
  horizontalDistance: number;
  maxElementsInRow: number;
  minScale: number;
  maxScale: number;
}

export function displayElementsInRows(elements: Container[], config: RowsConfig): void {
  const numberOfRows = Math.ceil(elements.length / config.maxElementsInRow);

  // Decide how to arrange the elements based on how many rows we have
  const elementsInRow: number[] = [];
  let processed = 0;
  for (let index = 0; index < numberOfRows; index += 1) {
    const remaining = elements.length - processed;
    if (numberOfRows > 1 && index === numberOfRows - 2) {
      // Penultimate row, try to have the same amount of elements on the last two rows
      const balanced = Math.ceil(remaining * 0.5);
      elementsInRow.push(balanced);
      processed += balanced;
    } else if (processed + config.maxElementsInRow < elements.length) {
      elementsInRow.push(config.maxElementsInRow);
      processed += config.maxElementsInRow;
    } else {
      elementsInRow.push(remaining);
      processed += remaining;
    }
  }

  const maxElementWidth = config.containerWidth / elementsInRow[0];
  const maxElementHeight = config.containerHeight / numberOfRows; 
  const halfHeight = config.containerHeight * 0.5;

  let startIndex = 0;
  for (let rowIndex = 0; rowIndex < elementsInRow.length; rowIndex += 1) {
    const numberOfElements = elementsInRow[rowIndex];
    let posX = -(config.horizontalDistance + maxElementWidth) * ((numberOfElements - 1) * 0.5);
    
    for (let elementIndex = startIndex; elementIndex < startIndex + numberOfElements; elementIndex += 1) {
      const elem = elements[elementIndex];
      const elemWidth = elem.width === 0 ? 1 : elem.width;
      const elemHeight = elem.height === 0 ? 1 : elem.height;

      const elemScale = lerp(config.minScale, config.maxScale, Math.min(maxElementWidth / elemWidth, maxElementHeight / elemHeight));

      elem.props({
        x: posX,
        y: lerp(-halfHeight, halfHeight, numberOfRows > 1 ? (rowIndex / (numberOfRows - 1)) : 0.5),
        scaleXY: elemScale,
      });

      posX += config.horizontalDistance + maxElementWidth;
    }
    startIndex += numberOfElements;
  }
}
